import { useState } from 'react';
import ActivityTable from './ActivityTable.tsx';
import dayjs from 'dayjs';
import MonthDropdown from '@/components/ui/date-picker/MonthDropdown.tsx';
import BarChartTotal from './BarChartTotal.tsx';
import { useGetUsageLogsByModelAndTime } from '@/service/llm/usage-logs/hook.ts';
import {
  generateChartConfig,
  transformDataForChart,
} from './ActivityTable.tsx/util/index.ts';
import CardChartTotal from '@/components/ui/card-wrapper/CardChartTotal.tsx';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId.ts';
import { Button } from '@/components/ui/button.tsx';
import { downloadCSV } from '@/utils/csv.ts';
import { getUsageLogsByModelAndTime } from '@/service/llm/usage-logs/index.ts';

const LLMActivity = () => {
  const { workspaceId } = useParamWorkspaceId();
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const handleSelect = (selectedMonth: string) => {
    setMonth(selectedMonth);
  };

  const { data } = useGetUsageLogsByModelAndTime({
    start_date: dayjs(month).startOf('month').unix(),
    end_date: dayjs(month).endOf('month').unix(),
    time_unit: 'day',
    workspace_id_filter: workspaceId,
  });

  const chartConfig = generateChartConfig(Array.from(new Set(data?.map((item) => item.model))) || []);

  const handleDownloadCSV = async () => {
    const result = await getUsageLogsByModelAndTime({
      start_date: dayjs(month).startOf('month').unix(),
      end_date: dayjs(month).endOf('month').unix(),
      time_unit: 'hour',
      workspace_id_filter: workspaceId,
    });
    downloadCSV({
      data: result,
      filename: `llm-activity-${month}.csv`,
    });
  };

  return (
    <div className="container">
      <p className="text-lg font-semibold">Activity</p>
      <div className="mt-4 flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <MonthDropdown onSelect={handleSelect} defaultMonth={month} />
          <Button variant={'secondary'} onClick={handleDownloadCSV}>
            Download CSV
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <CardChartTotal
            title="Prompt"
            description="Total Prompt Usage"
            total={
              data
                ?.reduce((acc, item) => acc + item.total_prompt_usage, 0)
                .toLocaleString() || 0
            }
          >
            <BarChartTotal
              chartData={transformDataForChart(
                data || [],
                'total_prompt_usage'
              )}
              chartConfig={chartConfig}
              startTime={dayjs(month).startOf('month').unix()}
              latestTime={dayjs(month).endOf('month').unix()}
            />
          </CardChartTotal>
          <CardChartTotal
            title="Completion"
            description="Total Completion Usage"
            total={
              data
                ?.reduce((acc, item) => acc + item.total_completion_usage, 0)
                .toLocaleString() || 0
            }
          >
            <BarChartTotal
              chartData={transformDataForChart(
                data || [],
                'total_completion_usage'
              )}
              chartConfig={chartConfig}
              startTime={dayjs(month).startOf('month').unix()}
              latestTime={dayjs(month).endOf('month').unix()}
            />
          </CardChartTotal>
          <CardChartTotal
            title="Requests"
            description="Total Usage REST Requests"
            total={
              data
                ?.reduce((acc, item) => acc + item.count, 0)
                .toLocaleString() || 0
            }
          >
            <BarChartTotal
              chartData={transformDataForChart(data || [], 'count')}
              chartConfig={chartConfig}
              startTime={dayjs(month).startOf('month').unix()}
              latestTime={dayjs(month).endOf('month').unix()}
            />
          </CardChartTotal>
        </div>
        <ActivityTable month={month} />
      </div>
    </div>
  );
};

export default LLMActivity;
